import { useEffect, useState } from "react";
import { ChevronDown, Search, Trophy, X } from "lucide-react";
import { toast } from "sonner";

import LoadingScreen from "../Utils/LoadingScreen";
import useReports from "../../Sevices/useReports";

const DriverPointsTab = ({ drivers = [] }) => {
  const { fetchDriverDetailsPerformancePoints, detailsPerformanceLoading } =
    useReports();

  const today = new Date().toISOString().split("T")[0];

  // =====================================================
  // STATES
  // =====================================================

  const [pointsFilterType, setPointsFilterType] = useState("weekly");

  const [pointsDriverId, setPointsDriverId] = useState("");
  const [pointsSearch, setPointsSearch] = useState("");

  const [driverPointsDetails, setDriverPointsDetails] = useState([]);

  const [showPointsDriverDropdown, setShowPointsDriverDropdown] =
    useState(false);

  const [pointsDriverDropdownSearch, setPointsDriverDropdownSearch] =
    useState("");

  const [pointsFromDate, setPointsFromDate] = useState(today);
  const [pointsToDate, setPointsToDate] = useState(today);
  const [pointsWeekDate, setPointsWeekDate] = useState(today);

  const [allDriverPointsLoading, setAllDriverPointsLoading] = useState(false);

  const [pointsCurrentPage, setPointsCurrentPage] = useState(1);

  const pointsPerPage = 25;

  // =====================================================
  // WEEK RANGE
  // =====================================================

  const getWeekRange = (selectedDate) => {
    const date = new Date(`${selectedDate}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
      return {
        fromDate: selectedDate,
        toDate: selectedDate,
      };
    }

    const day = date.getDay();

    const differenceFromMonday = day === 0 ? -6 : 1 - day;

    const monday = new Date(date);

    monday.setDate(date.getDate() + differenceFromMonday);

    const sunday = new Date(monday);

    sunday.setDate(monday.getDate() + 6);

    return {
      fromDate: monday.toISOString().split("T")[0],
      toDate: sunday.toISOString().split("T")[0],
    };
  };

  const selectedPointsWeek = getWeekRange(pointsWeekDate);

  // =====================================================
  // DATE RANGE
  // =====================================================

  const getPointsDateRange = () => {
    if (pointsFilterType === "weekly") {
      return getWeekRange(pointsWeekDate);
    }

    return {
      fromDate: pointsFromDate,
      toDate: pointsToDate,
    };
  };

  // =====================================================
  // NORMALIZE API RESPONSE
  // =====================================================

  const normalizeDriverPointsResponse = (apiData = []) => {
    if (!Array.isArray(apiData)) return [];

    return apiData.flatMap((dateGroup) => {
      const date = dateGroup?.date || "N/A";

      const dateDrivers = Array.isArray(dateGroup?.drivers)
        ? dateGroup.drivers
        : [];

      return dateDrivers.flatMap((driver) => {
        const driverCode = driver?.driver_code || "N/A";

        const driverName = driver?.name?.trim() || "Unknown Driver";

        const attendancePoints = Number(driver?.attendance_points || 0);

        const dutyTimingPoints = Number(driver?.duty_timing_points || 0);

        const details = Array.isArray(driver?.details) ? driver.details : [];

        const detailRows = details.map((detail) => ({
          id: `detail-${date}-${driverCode}-${detail?.pk_driver_detail_id}`,

          rowType: "detail",

          date,

          driverId: driverCode,
          driverCode,
          driverName,

          attendancePoints,
          dutyTimingPoints,

          detailId: detail?.pk_driver_detail_id,

          taskId: detail?.task_id,

          reason: detail?.reason || "N/A",

          points: Number(detail?.points || 0),
        }));

        const attendanceRow = {
          id: `attendance-${date}-${driverCode}`,

          rowType: "attendance",

          date,

          driverId: driverCode,
          driverCode,
          driverName,

          attendancePoints,
          dutyTimingPoints,

          detailId: null,
          taskId: null,

          reason: "Attendance",

          points: attendancePoints,
        };

        const dutyTimingRow = {
          id: `duty-${date}-${driverCode}`,

          rowType: "duty_timing",

          date,

          driverId: driverCode,
          driverCode,
          driverName,

          attendancePoints,
          dutyTimingPoints,

          detailId: null,
          taskId: null,

          reason: "Duty Timing",

          points: dutyTimingPoints,
        };

        return [attendanceRow, dutyTimingRow, ...detailRows];
      });
    });
  };

  // =====================================================
  // DRIVER DROPDOWN FILTER
  // =====================================================

  const filteredPointsDrivers = (drivers || []).filter((driver) => {
    const query = pointsDriverDropdownSearch.trim().toLowerCase();

    if (!query) return true;

    return [
      driver?.fullName,
      driver?.first_name,
      driver?.last_name,
      driver?.driverId,
      driver?.username,
      driver?.phone,
      driver?.email,
    ].some((value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .includes(query),
    );
  });

  // =====================================================
  // FETCH DRIVER POINTS
  // =====================================================

  const fetchPointsByDriverId = async (driverId = "") => {
    const { fromDate: apiFromDate, toDate: apiToDate } = getPointsDateRange();

    if (!apiFromDate || !apiToDate) {
      toast.error("Please select date range");

      return;
    }

    if (new Date(apiFromDate) > new Date(apiToDate)) {
      toast.error("From date cannot be greater than to date");

      return;
    }

    const data = await fetchDriverDetailsPerformancePoints(
      driverId || "",
      apiFromDate,
      apiToDate,
    );

    setDriverPointsDetails(normalizeDriverPointsResponse(data));

    setPointsCurrentPage(1);
  };

  // =====================================================
  // FETCH ALL DRIVERS
  // =====================================================

  const fetchAllDriversPoints = async () => {
    setAllDriverPointsLoading(true);

    try {
      await fetchPointsByDriverId("");
    } finally {
      setAllDriverPointsLoading(false);
    }
  };

  // =====================================================
  // SEARCH DRIVER
  // =====================================================

  const handlePointsSearch = async () => {
    const query = pointsSearch.trim().toLowerCase();

    if (!query) {
      setPointsDriverId("");

      setPointsDriverDropdownSearch("");

      setShowPointsDriverDropdown(false);

      await fetchPointsByDriverId("");

      return;
    }

    const driverList = Array.isArray(drivers) ? drivers : [];

    const exactDriver = driverList.find((driver) =>
      [
        driver?.driverId,
        driver?.phone,
        driver?.username,
        driver?.fullName,
      ].some(
        (value) =>
          String(value || "")
            .trim()
            .toLowerCase() === query,
      ),
    );

    const partialDriver = driverList.find((driver) =>
      [
        driver?.fullName,
        driver?.first_name,
        driver?.last_name,
        driver?.driverId,
        driver?.username,
        driver?.phone,
        driver?.email,
      ].some((value) =>
        String(value || "")
          .trim()
          .toLowerCase()
          .includes(query),
      ),
    );

    const matchedDriver = exactDriver || partialDriver;

    if (!matchedDriver?.driverId) {
      toast.error("Driver not found");

      setDriverPointsDetails([]);

      setPointsCurrentPage(1);

      return;
    }

    const driverId = String(matchedDriver.driverId);

    setPointsDriverId(driverId);

    setPointsSearch(matchedDriver?.fullName?.trim() || driverId);

    setPointsDriverDropdownSearch("");

    setShowPointsDriverDropdown(false);

    await fetchPointsByDriverId(driverId);
  };

  // =====================================================
  // TOTALS
  // =====================================================

  const totalDriverPoints = driverPointsDetails.reduce(
    (sum, item) => sum + Number(item?.points || 0),
    0,
  );

  const positiveDriverPoints = driverPointsDetails.reduce((sum, item) => {
    const value = Number(item?.points || 0);

    return value > 0 ? sum + value : sum;
  }, 0);

  const negativeDriverPoints = driverPointsDetails.reduce((sum, item) => {
    const value = Number(item?.points || 0);

    return value < 0 ? sum + value : sum;
  }, 0);

  // =====================================================
  // PAGINATION
  // =====================================================

  const pointsTotalPages = Math.max(
    1,
    Math.ceil(driverPointsDetails.length / pointsPerPage),
  );

  const pointsIndexOfLast = pointsCurrentPage * pointsPerPage;

  const pointsIndexOfFirst = pointsIndexOfLast - pointsPerPage;

  const currentDriverPoints = driverPointsDetails.slice(
    pointsIndexOfFirst,
    pointsIndexOfLast,
  );

  // =====================================================
  // INITIAL FETCH
  // =====================================================

  useEffect(() => {
    fetchAllDriversPoints();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =====================================================
  // PAGE VALIDATION
  // =====================================================

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(driverPointsDetails.length / pointsPerPage),
    );

    if (pointsCurrentPage > totalPages) {
      setPointsCurrentPage(totalPages);
    }
  }, [driverPointsDetails.length, pointsCurrentPage]);

  return (
    <div className="space-y-5">
      {/* =====================================================
          FILTERS
      ===================================================== */}

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-gray-900">
            Driver Performance Points
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            View performance points weekly or using a custom date range.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,2fr)_180px_150px]">
          {/* DRIVER */}

          <div className="min-w-0">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Driver
            </label>

            <div className="flex items-center gap-2">
              {/* SEARCH */}

              <div className="relative min-w-0 flex-1">
                <Search
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  type="text"
                  value={pointsSearch}
                  onChange={(event) => {
                    setPointsSearch(event.target.value);

                    setPointsDriverId("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handlePointsSearch();
                    }
                  }}
                  placeholder="Search name, driver code or phone..."
                  className="h-10 w-full rounded-lg border border-gray-200 pl-9 pr-9 text-xs outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                />

                {pointsSearch && (
                  <button
                    type="button"
                    onClick={async () => {
                      setPointsSearch("");

                      setPointsDriverId("");

                      setPointsDriverDropdownSearch("");

                      await fetchPointsByDriverId("");
                    }}
                    className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={handlePointsSearch}
                disabled={detailsPerformanceLoading || allDriverPointsLoading}
                className="h-10 shrink-0 rounded-lg bg-orange-500 px-4 text-xs font-bold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {detailsPerformanceLoading || allDriverPointsLoading
                  ? "Searching..."
                  : "Search"}
              </button>

              {/* DRIVER DROPDOWN */}

              <div className="relative w-[210px] shrink-0">
                <button
                  type="button"
                  onClick={() => setShowPointsDriverDropdown((prev) => !prev)}
                  className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 text-left text-xs outline-none hover:border-orange-300 focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                >
                  <span className="truncate">
                    {pointsDriverId
                      ? (drivers || []).find(
                          (driver) =>
                            String(driver?.driverId) === String(pointsDriverId),
                        )?.fullName || pointsDriverId
                      : "All Drivers"}
                  </span>

                  <ChevronDown
                    size={14}
                    className={`shrink-0 text-gray-400 transition ${
                      showPointsDriverDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showPointsDriverDropdown && (
                  <div className="absolute right-0 top-full z-[100] mt-1 max-h-[320px] w-[280px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
                    {/* DROPDOWN SEARCH */}

                    <div className="border-b border-gray-100 p-2">
                      <div className="relative">
                        <Search
                          size={14}
                          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                        />

                        <input
                          type="text"
                          value={pointsDriverDropdownSearch}
                          onChange={(event) =>
                            setPointsDriverDropdownSearch(event.target.value)
                          }
                          placeholder="Filter drivers..."
                          className="h-9 w-full rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-3 text-xs outline-none focus:border-orange-400 focus:bg-white"
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* ALL DRIVERS */}

                    <button
                      type="button"
                      onClick={async () => {
                        setPointsDriverId("");

                        setPointsSearch("");

                        setPointsDriverDropdownSearch("");

                        setShowPointsDriverDropdown(false);

                        await fetchPointsByDriverId("");
                      }}
                      className={`flex w-full items-center justify-between border-b border-gray-100 px-3 py-2.5 text-left text-xs font-semibold ${
                        !pointsDriverId
                          ? "bg-orange-50 text-orange-700"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span>All Drivers</span>

                      {!pointsDriverId && (
                        <span className="rounded-full bg-orange-500 px-2 py-0.5 text-[10px] text-white">
                          Selected
                        </span>
                      )}
                    </button>

                    {/* DRIVER LIST */}

                    <div className="max-h-[228px] overflow-y-auto overscroll-contain p-1">
                      {filteredPointsDrivers.length === 0 ? (
                        <div className="px-3 py-5 text-center text-xs text-gray-400">
                          No driver found
                        </div>
                      ) : (
                        filteredPointsDrivers.map((driver) => {
                          const active =
                            String(pointsDriverId) === String(driver?.driverId);

                          return (
                            <button
                              key={driver?.driverId || driver?.id}
                              type="button"
                              onClick={async () => {
                                const driverId = String(driver?.driverId || "");

                                setPointsDriverId(driverId);

                                setPointsSearch(
                                  driver?.fullName?.trim() || driverId,
                                );

                                setPointsDriverDropdownSearch("");

                                setShowPointsDriverDropdown(false);

                                await fetchPointsByDriverId(driverId);
                              }}
                              className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-1.5 text-left text-xs ${
                                active
                                  ? "bg-orange-50 text-orange-700"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <div className="min-w-0">
                                <p className="truncate font-semibold">
                                  {driver?.fullName?.trim() || "Unnamed Driver"}
                                </p>

                                <p className="mt-0.5 truncate text-[10px] text-gray-400">
                                  {driver?.driverId || "N/A"}

                                  {driver?.phone ? ` • ${driver.phone}` : ""}
                                </p>
                              </div>

                              {active && (
                                <span className="shrink-0 rounded-full bg-orange-500 px-2 py-0.5 text-[9px] font-bold text-white">
                                  Selected
                                </span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* VIEW TYPE */}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              View Type
            </label>

            <select
              value={pointsFilterType}
              onChange={(event) => {
                setPointsFilterType(event.target.value);

                setDriverPointsDetails([]);
              }}
              className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            >
              <option value="weekly">Weekly Wise</option>

              <option value="date">Date Wise</option>
            </select>
          </div>

          {/* APPLY */}

          <div className="flex items-end">
            <button
              type="button"
              onClick={() => fetchPointsByDriverId(pointsDriverId || "")}
              disabled={detailsPerformanceLoading || allDriverPointsLoading}
              className="h-10 w-full rounded-lg bg-orange-500 px-4 text-xs font-bold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {detailsPerformanceLoading || allDriverPointsLoading
                ? "Loading..."
                : "Apply Filter"}
            </button>
          </div>
        </div>

        {/* =====================================================
            WEEKLY FILTER
        ===================================================== */}

        {pointsFilterType === "weekly" && (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Select Any Date of Week
              </label>

              <input
                type="date"
                value={pointsWeekDate}
                max={today}
                onChange={(event) => {
                  setPointsWeekDate(event.target.value);

                  setDriverPointsDetails([]);
                }}
                className="h-11 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>

            <div className="rounded-xl border border-orange-100 bg-orange-50 p-3">
              <p className="text-xs font-medium text-orange-600">Week From</p>

              <p className="mt-1 text-sm font-bold text-orange-800">
                {selectedPointsWeek.fromDate}
              </p>
            </div>

            <div className="rounded-xl border border-orange-100 bg-orange-50 p-3">
              <p className="text-xs font-medium text-orange-600">Week To</p>

              <p className="mt-1 text-sm font-bold text-orange-800">
                {selectedPointsWeek.toDate}
              </p>
            </div>
          </div>
        )}

        {/* =====================================================
            DATE FILTER
        ===================================================== */}

        {pointsFilterType === "date" && (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                From Date
              </label>

              <input
                type="date"
                value={pointsFromDate}
                max={pointsToDate || today}
                onChange={(event) => {
                  setPointsFromDate(event.target.value);

                  setDriverPointsDetails([]);
                }}
                className="h-11 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                To Date
              </label>

              <input
                type="date"
                value={pointsToDate}
                min={pointsFromDate}
                max={today}
                onChange={(event) => {
                  setPointsToDate(event.target.value);

                  setDriverPointsDetails([]);
                }}
                className="h-11 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>
          </div>
        )}
      </div>

      {/* =====================================================
          LOADING / RESULTS
      ===================================================== */}

      {detailsPerformanceLoading ? (
        <LoadingScreen />
      ) : (
        <>
          {/* SUMMARY */}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-2xl border bg-white p-4 shadow-sm">
              <p className="text-sm text-gray-500">Records</p>

              <h3 className="mt-1 text-3xl font-bold text-gray-900">
                {driverPointsDetails.length}
              </h3>
            </div>

            <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4 shadow-sm">
              <p className="text-sm text-orange-700">Total Points</p>

              <h3
                className={`mt-1 text-3xl font-bold ${
                  totalDriverPoints >= 0 ? "text-emerald-700" : "text-red-700"
                }`}
              >
                {totalDriverPoints > 0
                  ? `+${totalDriverPoints}`
                  : totalDriverPoints}
              </h3>
            </div>

            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 shadow-sm">
              <p className="text-sm text-emerald-700">Positive Points</p>

              <h3 className="mt-1 text-3xl font-bold text-emerald-700">
                +{positiveDriverPoints}
              </h3>
            </div>

            <div className="rounded-2xl border border-red-100 bg-red-50 p-4 shadow-sm">
              <p className="text-sm text-red-700">Negative Points</p>

              <h3 className="mt-1 text-3xl font-bold text-red-700">
                {negativeDriverPoints}
              </h3>
            </div>
          </div>

          {/* =====================================================
              TABLE
          ===================================================== */}

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 p-4">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Performance Point Details
                </h3>

                <p className="mt-1 text-xs text-gray-500">
                  {pointsFilterType === "weekly"
                    ? `${selectedPointsWeek.fromDate} to ${selectedPointsWeek.toDate}`
                    : `${pointsFromDate} to ${pointsToDate}`}
                </p>
              </div>
            </div>

            {driverPointsDetails.length === 0 ? (
              <div className="flex min-h-[220px] flex-col items-center justify-center text-center">
                <Trophy className="mb-3 h-10 w-10 text-gray-300" />

                <p className="text-sm font-semibold text-gray-700">
                  No performance points found
                </p>

                <p className="mt-1 text-xs text-gray-500">
                  Select driver and date filter, then click Apply Filter.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-orange-400 text-left text-white">
                    <tr>
                      <th className="px-4 py-3">#</th>

                      <th className="px-4 py-3">Date</th>

                      <th className="px-4 py-3">Driver</th>

                      <th className="px-4 py-3">Task ID</th>

                      <th className="px-4 py-3">Reason</th>

                      <th className="px-4 py-3">Points</th>

                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {currentDriverPoints.map((item, index) => {
                      const pointValue = Number(item?.points || 0);

                      return (
                        <tr
                          key={item?.id || index}
                          className="hover:bg-orange-50/40"
                        >
                          <td className="px-4 py-3 text-gray-500">
                            {pointsIndexOfFirst + index + 1}
                          </td>

                          <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-800">
                            {item?.date || "N/A"}
                          </td>

                          <td className="px-4 py-3">
                            <p className="font-semibold text-gray-900">
                              {item?.driverName || "Unknown Driver"}
                            </p>

                            <p className="text-xs text-gray-500">
                              {item?.driverCode || "N/A"}
                            </p>
                          </td>

                          <td className="px-4 py-3 font-medium text-gray-700">
                            {item?.taskId || "—"}
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                item?.rowType === "attendance"
                                  ? "bg-blue-50 text-blue-700"
                                  : item?.rowType === "duty_timing"
                                    ? "bg-purple-50 text-purple-700"
                                    : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {item?.reason || "N/A"}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                                pointValue > 0
                                  ? "bg-emerald-50 text-emerald-700"
                                  : pointValue < 0
                                    ? "bg-red-50 text-red-700"
                                    : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {pointValue > 0 ? `+${pointValue}` : pointValue}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                                pointValue > 0
                                  ? "bg-green-100 text-green-700"
                                  : pointValue < 0
                                    ? "bg-red-100 text-red-700"
                                    : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {pointValue > 0
                                ? "Added"
                                : pointValue < 0
                                  ? "Deducted"
                                  : "No Change"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* PAGINATION */}

                <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold">
                      {pointsIndexOfFirst + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold">
                      {Math.min(pointsIndexOfLast, driverPointsDetails.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold">
                      {driverPointsDetails.length}
                    </span>{" "}
                    records
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setPointsCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={pointsCurrentPage === 1}
                      className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Previous
                    </button>

                    <span className="rounded-lg bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-700">
                      Page {pointsCurrentPage} of {pointsTotalPages}
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        setPointsCurrentPage((prev) =>
                          Math.min(pointsTotalPages, prev + 1),
                        )
                      }
                      disabled={pointsCurrentPage === pointsTotalPages}
                      className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DriverPointsTab;
